import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { headcountAPI } from "../api/client";
import { mayPerformWithRole } from "../utils/userAccess";
import EngineeringIcon from "@mui/icons-material/Engineering";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import GroupIcon from "@mui/icons-material/Group";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import BusinessIcon from "@mui/icons-material/Business";
import WcIcon from "@mui/icons-material/Wc";

import ChairIcon from "@mui/icons-material/Chair";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import BadgeIcon from "@mui/icons-material/Badge";
import DashboardIcon from "@mui/icons-material/Dashboard";
import "./HeadcountDashboard.css";
import LoadingButton from "../components/LoadingButton";

const AUTO_REFRESH_MS = Number(process.env.REACT_APP_HEADCOUNT_AUTO_REFRESH_MS || 4 * 60 * 60 * 1000);
const JOIN_DATE_KEYS = ["date_of_joining", "joining_date", "join_date", "hire_date", "start_date", "dateOfJoining"];
const EXIT_DATE_KEYS = ["date_of_exit", "exit_date", "termination_date", "last_working_day", "dateOfExit"];
// The base employee population shown on the dashboard: active employees plus
// employees on Secondment (Payroll Transfer). Drill-downs reuse this exact
// status bucket so DISPLAYED COUNT = DRILL-DOWN RESULT.
const HEADCOUNT_EMPLOYEE_STATUS = "Active,Secondment (Payroll Transfer)";
const MONTH_OPTIONS = [
  { value: "01", label: "January" },
  { value: "02", label: "February" },
  { value: "03", label: "March" },
  { value: "04", label: "April" },
  { value: "05", label: "May" },
  { value: "06", label: "June" },
  { value: "07", label: "July" },
  { value: "08", label: "August" },
  { value: "09", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];

// Mirrors the backend Content-Disposition name so direct API downloads and
// dashboard downloads produce the same, self-describing filename:
//   Headcount_All_20261031.xlsx
//   Headcount_Active_Engineering_20261031.csv
function buildHeadcountExportFilename(scope, category, ext) {
  const now = new Date();
  const stamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}${String(now.getDate()).padStart(2, "0")}`;
  const scopeLabel = scope === "active" ? "Active" : "All";
  const categoryLabel = category === "engineering"
    ? "_Engineering"
    : category === "support"
      ? "_Support"
      : "";
  return `Headcount_${scopeLabel}${categoryLabel}_${stamp}.${ext}`;
}

export default function HeadcountDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const [kpiFilters, setKpiFilters] = useState({ year: "", month: "" });
  const [expandedDivisions, setExpandedDivisions] = useState({});
  const [expandedDepartments, setExpandedDepartments] = useState({});
  const [summaryCategory, setSummaryCategory] = useState("all");
  const [kpiCategory, setKpiCategory] = useState("all");
  const [trendCategory, setTrendCategory] = useState("all");
  const [trendTooltip, setTrendTooltip] = useState(null);
  const [guideVisible, setGuideVisible] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportFormat, setExportFormat] = useState("xlsx");
  const [excelReportMenuOpen, setExcelReportMenuOpen] = useState(false);
  const [primaryFilter, setPrimaryFilter] = useState("employee");
  const fileInputRef = useRef(null);

  const toggleDivision = useCallback((key) => {
    setExpandedDivisions((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  }, []);

  const toggleDepartment = useCallback((key) => {
    setExpandedDepartments((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  }, []);

  const loadDashboard = async () => {
    setLoading(true);
    setError("");
    try {
      const dashboardRes = await headcountAPI.getDashboard();
      setData(dashboardRes.data || null);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load headcount dashboard.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  const runSync = async (forceFull = false, silent = false) => {
    if (!silent) {
      setSyncing(true);
      setError("");
    }
    try {
      await headcountAPI.sync({ force_full: forceFull });
      await loadDashboard();
    } catch (err) {
      setError(err?.response?.data?.message || "Sync failed.");
    } finally {
      if (!silent) setSyncing(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await headcountAPI.uploadExcel(formData);
      await loadDashboard();
    } catch (err) {
      setError(err?.response?.data?.message || "Upload failed.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleExport = async (scope = "all", exportCategory = summaryCategory, format = exportFormat) => {
    setExcelReportMenuOpen(false);
    setExporting(true);
    try {
      const res = await headcountAPI.exportData(format, {
        tab: activeTab,
        category: exportCategory,
        year: kpiFilters.year,
        month: kpiFilters.month,
        exportScope: scope,
      });
      const ext = format === "csv" ? "csv" : "xlsx";
      const mime = format === "csv"
        ? "text/csv;charset=utf-8;"
        : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const blob = new Blob([res.data], { type: mime });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = buildHeadcountExportFilename(scope, exportCategory, ext);
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err?.response?.data?.message || "Export failed.");
    } finally {
      setExporting(false);
    }
  };

  const loadDashboardRef = useRef(loadDashboard);
  loadDashboardRef.current = loadDashboard;

  useEffect(() => {
    if (!AUTO_REFRESH_MS || AUTO_REFRESH_MS < 60_000) return undefined;
    const timer = setInterval(async () => {
      try {
        await headcountAPI.sync({ force_full: false });
        await loadDashboardRef.current();
      } catch {
        // keep dashboard usable even if scheduled refresh fails
      }
    }, AUTO_REFRESH_MS);
    return () => clearInterval(timer);
  }, []);

  const applyKpiFilters = useCallback((employees) => {
    return employees.filter((e) => {
      if (!matchesPeriod(e, kpiFilters.year, kpiFilters.month)) return false;
      return true;
    });
  }, [kpiFilters]);

  const activeEmployees = useMemo(
    () => (data?.active_employees || []).map((e) => ({
      ...e,
      employment_type: normalizeEmploymentType(e.employment_type),
    })),
    [data]
  );

  const exitedEmployees = useMemo(
    () => (data?.exited_employees || []).map((e) => ({
      ...e,
      employment_type: normalizeEmploymentType(e.employment_type),
    })),
    [data]
  );

  const allEmployees = useMemo(
    () => [...activeEmployees, ...exitedEmployees],
    [activeEmployees, exitedEmployees]
  );

  // Interns/trainees come from the same Zoho headcount feed as every other
  // employee (employment_type = Intern/Trainee), so they are identified by
  // classification instead of a second data source. This removes the old
  // double count where Zoho rows and local trainee rows both appeared.
  const internEmployees = useMemo(
    () => allEmployees.filter(isTraineeRecord),
    [allEmployees]
  );

  const allPeople = allEmployees;

  // Current headcount population = regular active employees + PT/Secondment
  // employees + active interns/trainees. Each person is bucketed exactly once
  // so nobody is double counted as both Employee and Trainee/PT.
  const employeeParticipants = useMemo(
    () => [
      ...activeEmployees.filter((e) => !isPTEmployee(e) && !isTraineeRecord(e)),
      ...exitedEmployees.filter((e) => isPTEmployee(e)),
    ],
    [activeEmployees, exitedEmployees]
  );

  // Active interns/trainees from the Zoho feed. Only active records count
  // toward the live headcount population shown in the dashboard sections below.
  const internParticipants = useMemo(
    () => activeEmployees.filter(isTraineeRecord),
    [activeEmployees]
  );

  const participantEmployees = useMemo(
    () => [...employeeParticipants, ...internParticipants],
    [employeeParticipants, internParticipants]
  );

  const globallyFilteredEmployees = useMemo(
    () => applyKpiFilters(participantEmployees),
    [participantEmployees, applyKpiFilters]
  );

  // Primary dataset for the sections below the Employee/Intern toggle. It drives
  // every calculation and breakdown (department, company, gender, employment
  // type, seating) off whichever source is selected, never a mix of both.
  const sourceParticipants = useMemo(
    () => (primaryFilter === "intern" ? internParticipants : employeeParticipants),
    [primaryFilter, internParticipants, employeeParticipants]
  );

  const sourceFilteredEmployees = useMemo(
    () => applyKpiFilters(sourceParticipants),
    [sourceParticipants, applyKpiFilters]
  );

  const filteredBreakdowns = useMemo(
    () => buildBreakdownsFromEmployees(globallyFilteredEmployees),
    [globallyFilteredEmployees]
  );
  const trendData = useMemo(() => data?.monthly_trend || [], [data]);

  const kpiEmployees = useMemo(() => {
    if (kpiCategory === "engineering") return globallyFilteredEmployees.filter(isEngineeringEmployee);
    if (kpiCategory === "support") return globallyFilteredEmployees.filter(isSupportEmployee);
    return globallyFilteredEmployees;
  }, [globallyFilteredEmployees, kpiCategory]);

  const { engineeringBreakdown, supportBreakdown, totalBreakdown, unspecifiedCount } = useMemo(() => {
    const eng = [];
    const sup = [];
    let unspec = 0;
    for (let i = 0; i < kpiEmployees.length; i++) {
      const record = kpiEmployees[i];
      if (isEngineeringEmployee(record)) eng.push(record);
      else if (isSupportEmployee(record)) sup.push(record);
      else unspec += 1;
    }
    return {
      engineeringBreakdown: splitBreakdown(eng, { category: "engineering" }),
      supportBreakdown: splitBreakdown(sup, { category: "support" }),
      totalBreakdown: splitBreakdown(kpiEmployees),
      unspecifiedCount: unspec,
    };
  }, [kpiEmployees]);

  const recentJoiners = useMemo(() => {
    const month = kpiFilters.month || String(new Date().getMonth() + 1).padStart(2, "0");
    const year = kpiFilters.year || String(new Date().getFullYear());
    return allPeople
      .filter((e) => {
        const d = getFirstDate(e, JOIN_DATE_KEYS);
        return d && String(d.getFullYear()) === year && String(d.getMonth() + 1).padStart(2, "0") === month;
      })
      .slice(0, 10);
  }, [allPeople, kpiFilters]);

  const recentExits = useMemo(() => {
    const month = kpiFilters.month || String(new Date().getMonth() + 1).padStart(2, "0");
    const year = kpiFilters.year || String(new Date().getFullYear());
    return allPeople
      .filter((e) => {
        const d = getFirstDate(e, EXIT_DATE_KEYS);
        return d && String(d.getFullYear()) === year && String(d.getMonth() + 1).padStart(2, "0") === month;
      })
      .slice(0, 10);
  }, [allPeople, kpiFilters]);

  const { engineeringEmployees, supportEmployees, categoryEmployees } = useMemo(() => {
    const eng = [], sup = [];
    for (let i = 0; i < sourceFilteredEmployees.length; i++) {
      const e = sourceFilteredEmployees[i];
      if (isEngineeringEmployee(e)) eng.push(e);
      else if (isSupportEmployee(e)) sup.push(e);
    }
    const cat = summaryCategory === "engineering" ? eng : summaryCategory === "support" ? sup : sourceFilteredEmployees;
    return { engineeringEmployees: eng, supportEmployees: sup, categoryEmployees: cat };
  }, [sourceFilteredEmployees, summaryCategory]);
  const categoryBreakdowns = useMemo(
    () => buildBreakdownsFromEmployees(categoryEmployees),
    [categoryEmployees]
  );
  const categoryOrgHierarchy = useMemo(
    () => buildOrgTree(categoryEmployees),
    [categoryEmployees]
  );

  const employmentTypeSummary = useMemo(() => {
    const map = new Map();
    categoryEmployees.forEach((record) => {
      const label = classifyEmploymentType(record);
      map.set(label, (map.get(label) || 0) + 1);
    });
    return Array.from(map.entries()).map(([label, value]) => ({ label, value })).sort((a, b) => b.value - a.value);
  }, [categoryEmployees]);

  const periodEmployees = useMemo(() => {
    if (kpiCategory === "engineering") return allPeople.filter(isEngineeringEmployee);
    if (kpiCategory === "support") return allPeople.filter(isSupportEmployee);
    return allPeople;
  }, [allPeople, kpiCategory]);

  const joinsForPeriod = useMemo(
    () => countEmployeesForDate(periodEmployees, JOIN_DATE_KEYS, kpiFilters.year, kpiFilters.month, data?.kpis?.joins_this_month),
    [periodEmployees, kpiFilters.year, kpiFilters.month, data]
  );
  const exitsForPeriod = useMemo(
    () => countEmployeesForDate(periodEmployees, EXIT_DATE_KEYS, kpiFilters.year, kpiFilters.month, data?.kpis?.exits_this_month),
    [periodEmployees, kpiFilters.year, kpiFilters.month, data]
  );

  const trendSourceEmployees = useMemo(
    () => (primaryFilter === "intern" ? internEmployees : allEmployees),
    [primaryFilter, internEmployees, allEmployees]
  );

  const trendEmployees = useMemo(() => {
    if (trendCategory === "all") return trendSourceEmployees;
    return trendSourceEmployees.filter(trendCategory === "engineering" ? isEngineeringEmployee : isSupportEmployee);
  }, [trendSourceEmployees, trendCategory]);

  const trendYear = kpiFilters.year || String(new Date().getFullYear());

  const trendChartData = useMemo(
    () => buildTrendData(trendEmployees, trendYear),
    [trendEmployees, trendYear]
  );

  const maxTrendValue = useMemo(() => {
    if (!trendChartData.length) return 1;
    return Math.max(...trendChartData.map((row) => Math.max(row.joins || 0, row.exits || 0)), 1);
  }, [trendChartData]);

  const sourceRoute = "/employees";

  // Base scope shared by every drill-down below. It reproduces the exact
  // population used to calculate dashboard counts: the selected source plus its
  // status bucket (Employees = Active + Secondment, Trainees = Active Zoho
  // employment types) plus the active KPI period filter. Each breakdown then
  // layers its own field filter on top so DISPLAYED COUNT = DRILL-DOWN RESULT.
  const getBaseScopeParams = useCallback(() => {
    const params = new URLSearchParams();
    if (primaryFilter === "intern") {
      // Zoho is the source of truth: trainees/interns are normal employee rows
      // whose employment_type matches these values on the Employees page.
      params.set("employmentType", "Intern,Trainee");
      params.set("status", "Active");
    } else {
      params.set("status", HEADCOUNT_EMPLOYEE_STATUS);
    }
    if (kpiFilters.year || kpiFilters.month) params.set("periodYear", kpiFilters.year || String(new Date().getFullYear()));
    if (kpiFilters.month) params.set("periodMonth", kpiFilters.month);
    return params;
  }, [primaryFilter, kpiFilters.year, kpiFilters.month]);

  const applyScopeExtraParams = useCallback((params, category, extra = {}) => {
    if (category && category !== "all") params.set("category", category);
    if (extra.department) params.set("department", extra.department);
    if (extra.division) params.set("division", extra.division);
    if (extra.businessUnit) params.set("businessUnit", extra.businessUnit);
    if (extra.company) params.set("company", extra.company);
    return params;
  }, []);

  const handleNavigate = useCallback((key, value, category) => {
    const params = getBaseScopeParams();
    applyScopeExtraParams(params, category);
    params.set(key, value);
    navigate(`${sourceRoute}?${params.toString()}`);
  }, [getBaseScopeParams, applyScopeExtraParams, sourceRoute, navigate]);

  const handleSummaryClick = useCallback((title, label) => {
    const map = {
      Gender: "gender",
      "Employment Type": "employmentType",
      "Seating Location": "seatingLocation",
      Company: "company",
    };
    const key = map[title];
    if (!key) return;
    handleNavigate(key, label, summaryCategory);
  }, [handleNavigate, summaryCategory]);

  const handleDeptClick = useCallback((type, label, category) => {
    const params = getBaseScopeParams();
    params.set(type === "division" ? "division" : "department", label);
    applyScopeExtraParams(params, category);
    navigate(`${sourceRoute}?${params.toString()}`);
  }, [getBaseScopeParams, applyScopeExtraParams, sourceRoute, navigate]);

  const handlePeopleClick = useCallback((kind, filters = {}) => {
    const params = getBaseScopeParams();
    applyScopeExtraParams(params, filters.category, filters);
    if (kind === "intern" || kind === "trainee") {
      params.set("employmentType", "Intern,Trainee");
      params.set("status", "Active");
      navigate(`/employees?${params.toString()}`);
      return;
    }
    if (kind === "pt") {
      params.set("status", PT_EMPLOYEE_STATUS);
    } else {
      params.set("status", "Active");
    }
    navigate(`/employees?${params.toString()}`);
  }, [getBaseScopeParams, applyScopeExtraParams, navigate]);

  const handleCategoryClick = useCallback((filters = {}) => {
    const params = getBaseScopeParams();
    if (filters.category) params.set("category", filters.category);
    if (filters.status) params.set("status", filters.status);
    navigate(`/employees?${params.toString()}`);
  }, [getBaseScopeParams, navigate]);

  const handleEmploymentTypeClick = useCallback((label) => {
    const params = getBaseScopeParams();
    applyScopeExtraParams(params, summaryCategory);
    if (label === "Intern" || label === "Trainee") {
      params.set("employmentType", "Intern,Trainee");
      params.set("status", "Active");
      navigate(`/employees?${params.toString()}`);
      return;
    }
    if (label === "Secondment / PT") {
      params.set("status", PT_EMPLOYEE_STATUS);
      navigate(`/employees?${params.toString()}`);
      return;
    }
    const typeKey = mapEmploymentTypeRoute(label);
    if (!typeKey) return;
    params.set("employmentType", typeKey);
    navigate(`/employees?${params.toString()}`);
  }, [getBaseScopeParams, applyScopeExtraParams, summaryCategory, navigate]);

  const canSync = mayPerformWithRole(data?.viewer?.role, "headcount", "sync", "headcount.sync");
  const canExport = mayPerformWithRole(data?.viewer?.role, "headcount", "export", "headcount.export");
  const canImport = mayPerformWithRole(data?.viewer?.role, "headcount", "import", "headcount.import");

  const yearOptions = useMemo(() => {
    const years = new Set();
    allPeople.forEach((e) => getEmployeeYears(e).forEach((y) => years.add(y)));
    trendData.forEach((row) => {
      const match = String(row?.month || "").match(/^(\d{4})/);
      if (match) years.add(match[1]);
    });
    return [...years].filter((y) => Number(y) >= 2026).sort((a, b) => b.localeCompare(a));
  }, [allPeople, trendData]);

  return (
    <div className="hc-layout">
      <div className="hc-header-bar">
        <h2 className="hc-title">UTS Group HC Dashboard</h2>
      </div>

      <div className="hc-board">
        {error ? <div className="hc-error">{error}</div> : null}
        {!error && data?.viewer?.role === "restricted" ? (
          <div className="hc-error">HC Dashboard access is restricted for this role. Contact your administrator.</div>
        ) : null}
        {loading ? <div className="hc-note">Loading dashboard data...</div> : null}

        {!loading && data ? (
          <>
            <div className="hc-filter-bar">
              <div className="hc-filter-left">
                <select className="hc-filter-select" value={kpiFilters.year || ""} onChange={(e) => setKpiFilters({ year: e.target.value, month: e.target.value ? kpiFilters.month : "" })}>
                  <option value="">All years</option>
                  {yearOptions.map((label) => (<option key={label} value={label}>{label}</option>))}
                </select>
                <select className="hc-filter-select" value={kpiFilters.month || ""} onChange={(e) => setKpiFilters({ ...kpiFilters, month: e.target.value })}>
                  <option value="">All months</option>
                  {MONTH_OPTIONS.map((item) => (<option key={item.value} value={item.value}>{item.label}</option>))}
                </select>
                {(kpiFilters.year || kpiFilters.month) && (
                  <button className="hc-clear-filter-btn" onClick={() => setKpiFilters({ year: "", month: "" })}>Clear</button>
                )}
              </div>
              <div className="hc-filter-right">
                <span className="hc-viewer-tag">
                  Role: {data?.viewer?.role || "-"} | Scope: {data?.viewer?.scope || "-"}{data?.viewer?.department ? ` | Dept: ${data.viewer.department}` : ""}
                </span>
                <span className="hc-last-refreshed">
                  Refreshed: {formatDateTime(data?.last_sync?.run_finished_at)}
                </span>
                <div className="hc-action-buttons">
                  <input type="file" ref={fileInputRef} onChange={handleFileUpload} accept=".xlsx,.xls,.csv" style={{ display: 'none' }} />
                  <LoadingButton type="button" className="hc-action-btn" onClick={() => fileInputRef.current?.click()} loading={uploading} loadingText="Uploading..." disabled={!canImport}>
                    Upload Excel
                  </LoadingButton>
                  <div className="hc-excel-report-menu">
                    <LoadingButton
                      type="button"
                      className="hc-action-btn"
                      onClick={() => setExcelReportMenuOpen((open) => !open)}
                      loading={exporting}
                      loadingText="..."
                      disabled={!canExport}
                      aria-expanded={excelReportMenuOpen}
                      aria-haspopup="menu"
                    >
                      Download Report <span className="hc-excel-report-chevron" aria-hidden="true">▾</span>
                    </LoadingButton>
                    {excelReportMenuOpen && (
                      <div className="hc-excel-report-options" aria-label="Headcount report options">
                        <div className="hc-export-format" role="group" aria-label="Export file format">
                          <button
                            type="button"
                            className={exportFormat === "xlsx" ? "active" : ""}
                            style={exportFormat === "xlsx" ? { background: "#0F4C81", color: "#FFFFFF" } : undefined}
                            aria-pressed={exportFormat === "xlsx"}
                            onClick={() => setExportFormat("xlsx")}
                          >
                            {exportFormat === "xlsx" ? "✓ " : ""}Excel
                          </button>
                          <button
                            type="button"
                            className={exportFormat === "csv" ? "active" : ""}
                            style={exportFormat === "csv" ? { background: "#0F4C81", color: "#FFFFFF" } : undefined}
                            aria-pressed={exportFormat === "csv"}
                            onClick={() => setExportFormat("csv")}
                          >
                            {exportFormat === "csv" ? "✓ " : ""}CSV
                          </button>
                        </div>
                        <button type="button" onClick={() => handleExport("all", "all", exportFormat)} disabled={exporting}>
                          All Headcount
                        </button>
                        <button type="button" onClick={() => handleExport("active", "all", exportFormat)} disabled={exporting}>
                          Active Headcount
                        </button>
                        <button type="button" onClick={() => handleExport("active", "engineering", exportFormat)} disabled={exporting}>
                          Engineering ({engineeringBreakdown.total})
                        </button>
                        <button type="button" onClick={() => handleExport("active", "support", exportFormat)} disabled={exporting}>
                          Support ({supportBreakdown.total})
                        </button>
                      </div>
                    )}
                  </div>
                  <LoadingButton type="button" className="hc-refresh-btn" onClick={() => runSync(false)} loading={syncing} loadingText="Refreshing..." disabled={!canSync}>
                    Sync
                  </LoadingButton>
                  <LoadingButton type="button" className="hc-action-btn hc-full-sync-btn" onClick={() => runSync(true)} loading={syncing} loadingText="Full syncing..." disabled={!canSync}>
                    Full Sync
                  </LoadingButton>
                </div>
              </div>
            </div>

            <div className="hc-kpi-row hc-business-units hc-main-banners">
              <KpiCard
                label="Engineering"
                value={engineeringBreakdown.total}
                icon={EngineeringIcon}
                className="hc-kpi-engineering"
                onClick={() => handleCategoryClick({ category: "engineering", status: HEADCOUNT_EMPLOYEE_STATUS })}
                breakdown={engineeringBreakdown}
                onBreakdownClick={handlePeopleClick}
              />
              <KpiCard
                label="Support"
                value={supportBreakdown.total}
                icon={SupportAgentIcon}
                className="hc-kpi-support"
                onClick={() => handleCategoryClick({ category: "support", status: HEADCOUNT_EMPLOYEE_STATUS })}
                breakdown={supportBreakdown}
                onBreakdownClick={handlePeopleClick}
              />
              {unspecifiedCount > 0 && (
                <KpiCard
                  label="Unspecified"
                  value={unspecifiedCount}
                  icon={InfoOutlinedIcon}
                  className="hc-kpi-unspecified"
                  onClick={() => handleCategoryClick({ category: "unspecified", status: "Active," + PT_EMPLOYEE_STATUS })}
                />
              )}
              <KpiCard
                label="Total"
                value={totalBreakdown.total}
                icon={GroupIcon}
                className="hc-kpi-total"
                onClick={() => handleCategoryClick({ status: HEADCOUNT_EMPLOYEE_STATUS })}
                breakdown={totalBreakdown}
                onBreakdownClick={handlePeopleClick}
              />
            </div>

            {/* Joiners/Exits ticker */}
            <JoinExitTicker
              joiners={recentJoiners}
              exits={recentExits}
              joinsForPeriod={joinsForPeriod}
              exitsForPeriod={exitsForPeriod}
            />

            <div className="hc-tabs">
              <button
                className={`hc-tab ${activeTab === "overview" ? "active" : ""}`}
                onClick={() => setActiveTab("overview")}
              >
                <DashboardIcon className="hc-tab-icon" /> Overview
              </button>
              <button
                className={`hc-tab ${activeTab === "trends" ? "active" : ""}`}
                onClick={() => setActiveTab("trends")}
              >
                <TrendingUpIcon className="hc-tab-icon" /> Trends
              </button>
            </div>

            <div className={`hc-guide-panel ${guideVisible ? "visible" : "hidden"}`}>
              <div className="hc-guide-header">
                <div className="hc-guide-title">
                  <InfoOutlinedIcon className="hc-guide-icon" /> Quick Guide
                </div>
                <button type="button" className="hc-guide-toggle" onClick={() => setGuideVisible((v) => !v)}>
                  {guideVisible ? "Hide Guide" : "Show Guide"}
                </button>
              </div>
              <div className="hc-guide-body">
                <ul>
                  <li>Click <strong>Engineering</strong> or <strong>Support</strong> to navigate to the Employees page filtered by division.</li>
                  <li>Click <strong>Company</strong>, <strong>Gender</strong>, <strong>Employment Type</strong> or <strong>Seating Location</strong> values to open filtered employee list.</li>
                  <li>Use <strong>Overview</strong> and <strong>Trends</strong> tabs to navigate.</li>
                  <li>Use <strong>Year</strong> and <strong>Month</strong> filters to analyze headcount by period.</li>
                </ul>
              </div>
            </div>

            <div className="hc-primary-filter">
              <span className="hc-primary-filter-label"><GroupIcon className="hc-primary-filter-icon" /> Population Source</span>
              <div className="hc-primary-toggle" role="group" aria-label="Population source filter">
                <button
                  type="button"
                  className={`hc-primary-btn ${primaryFilter === "employee" ? "active" : ""}`}
                  onClick={() => setPrimaryFilter("employee")}
                >
                  Employee
                </button>
                <button
                  type="button"
                  className={`hc-primary-btn ${primaryFilter === "intern" ? "active" : ""}`}
                  onClick={() => setPrimaryFilter("intern")}
                >
                  Trainee
                </button>
              </div>
            </div>

            {activeTab === "overview" && (
              <>

                <div className="hc-filter-bar">
                  <CategoryToggle
                    selected={summaryCategory}
                    onChange={setSummaryCategory}
                  />
                </div>

                <div className="hc-overview-split">
                  <div className="hc-overview-left">
                    {summaryCategory === "all" ? (
                      <div className="hc-overview-left-all">
                        <DepartmentSummaryCard
                          icon={<EngineeringIcon />}
                          title="Engineering"
                          total={categoryOrgHierarchy.engineering?.value || 0}
                          divisions={categoryOrgHierarchy.engineering?.children || []}
                          onDivisionClick={(label) => handleDeptClick("division", label, "engineering")}
                        />
                        <DepartmentSummaryCard
                          icon={<SupportAgentIcon />}
                          title="Support"
                          total={categoryOrgHierarchy.support?.value || 0}
                          divisions={categoryOrgHierarchy.support?.children || []}
                          onDivisionClick={(label) => handleDeptClick("division", label, "support")}
                        />
                      </div>
                    ) : summaryCategory === "engineering" ? (
                      <section className="hc-summary-panel hc-eng-panel">
                        <div className="hc-panel-title"><EngineeringIcon className="hc-title-icon" /> Engineering Divisions / Departments</div>
                        <EngineeringOverviewPanel
                          tree={categoryOrgHierarchy}
                          expandedDivisions={expandedDivisions}
                          expandedDepartments={expandedDepartments}
                          toggleDivision={toggleDivision}
                          toggleDepartment={toggleDepartment}
                          onDivisionClick={(label) => handleDeptClick("division", label, "engineering")}
                          onDepartmentClick={(label) => handleDeptClick("department", label, "engineering")}
                          onPeopleClick={handlePeopleClick}
                        />
                      </section>
                    ) : (
                      <section className="hc-summary-panel hc-supp-panel">
                        <div className="hc-panel-title"><SupportAgentIcon className="hc-title-icon" /> Support Divisions / Departments</div>
                        <SupportOverviewPanel
                          tree={categoryOrgHierarchy}
                          expandedDivisions={expandedDivisions}
                          expandedDepartments={expandedDepartments}
                          toggleDivision={toggleDivision}
                          toggleDepartment={toggleDepartment}
                          onDivisionClick={(label) => handleDeptClick("division", label, "support")}
                          onDepartmentClick={(label) => handleDeptClick("department", label, "support")}
                          onPeopleClick={handlePeopleClick}
                        />
                      </section>
                    )}
                  </div>

                  <div className="hc-overview-right">
                    <section className="hc-summary-panel hc-overview-right-card">
                      <div className="hc-panel-title"><BusinessIcon className="hc-title-icon" /> Company</div>
                      <div className="hc-summary-pills-vertical">
                        <SummaryBlock title="Company" items={categoryBreakdowns.by_company} onItemClick={(label) => handleSummaryClick("Company", label)} showAll />
                      </div>
                    </section>
                    <section className="hc-summary-panel hc-overview-right-card">
                      <div className="hc-panel-title"><WcIcon className="hc-title-icon" /> Gender</div>
                      <div className="hc-summary-pills-vertical">
                        <SummaryBlock title="Gender" items={(categoryBreakdowns.by_gender || []).map((item) => ({
                          ...item,
                          label: item.label === "Male" ? "♂ Male" : item.label === "Female" ? "♀ Female" : item.label,
                        }))} onItemClick={(label) => {
                          const clean = label.replace(/^[♂♀]\s*/, '');
                          handleSummaryClick("Gender", clean);
                        }} />
                      </div>
                    </section>
                    <section className="hc-summary-panel hc-overview-right-card">
                      <div className="hc-panel-title"><BadgeIcon className="hc-title-icon" /> Employment Type</div>
                      <div className="hc-summary-pills-vertical">
                        <SummaryBlock title="Employment Type" items={employmentTypeSummary} onItemClick={handleEmploymentTypeClick} />
                      </div>
                    </section>
                    <section className="hc-summary-panel hc-overview-right-card">
                      <div className="hc-panel-title"><ChairIcon className="hc-title-icon" /> Seating Location</div>
                      <div className="hc-summary-pills-vertical">
                        <SummaryBlock title="Seating Location" items={categoryBreakdowns.by_seating_location} onItemClick={(label) => handleSummaryClick("Seating Location", label)} showAll />
                      </div>
                    </section>
                  </div>
                </div>

              </>
            )}

            {activeTab === "trends" && (
              <section className="hc-trend-panel">
                <div className="hc-trend-header">
                  <div className="hc-panel-title">Monthly joiners / exit trend</div>
                  <div className="hc-trend-controls">
                    <div className="hc-header-pills">
                      {["all", "engineering", "support"].map((opt) => (
                        <button
                          key={opt}
                          type="button"
                          className={`hc-pill ${trendCategory === opt ? "active" : ""}`}
                          onClick={() => setTrendCategory(opt)}
                        >
                          {opt === "all" ? "All" : opt === "engineering" ? "Engineering" : "Support"}
                        </button>
                      ))}
                    </div>
                    <select
                      className="hc-filter-select"
                      value={kpiFilters.year || ""}
                      onChange={(e) => setKpiFilters({ ...kpiFilters, year: e.target.value })}
                    >
                      <option value="">All years</option>
                      {yearOptions.map((label) => (
                        <option key={label} value={label}>{label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="hc-trend-legend">
                  <span className="hc-legend-item"><span className="hc-legend-dot join" /> Joiners</span>
                  <span className="hc-legend-item"><span className="hc-legend-dot exit" /> Exits</span>
                </div>
                {trendChartData.length ? (
                  <div className="hc-trend-chart">
                    {trendChartData.map((item) => {
                      const joins = item.joins || 0;
                      const exits = item.exits || 0;
                      const maxVal = maxTrendValue || 1;
                      const joinH = Math.max(4, (joins / maxVal) * 120);
                      const exitH = exits ? Math.max(4, (exits / maxVal) * 120) : 0;
                      const showExit = exits > 0;
                      return (
                        <div
                          key={item.month}
                          className="hc-trend-column"
                          onMouseEnter={() => setTrendTooltip(item.month)}
                          onMouseLeave={() => setTrendTooltip(null)}
                        >
                          {trendTooltip === item.month && (
                            <div className="hc-trend-tooltip">
                              <div className="hc-trend-tooltip-month">{item.month}</div>
                              <div className="hc-trend-tooltip-row">Joiners: <strong>{joins}</strong></div>
                              <div className="hc-trend-tooltip-row">Exits: <strong>{exits}</strong></div>
                              <div className="hc-trend-tooltip-row">
                                Net: <strong style={{ color: item.net_change >= 0 ? "#10b981" : "#ef4444" }}>
                                  {item.net_change >= 0 ? "+" : ""}{item.net_change}
                                </strong>
                              </div>
                            </div>
                          )}
                          <div className="hc-trend-column-bars">
                            <div className="hc-trend-bar-wrap-vert">
                              <div className="hc-trend-bar-value">{joins || ""}</div>
                              <div className="hc-trend-bar join" style={{ height: joinH }} />
                            </div>
                            {showExit && (
                              <div className="hc-trend-bar-wrap-vert">
                                <div className="hc-trend-bar-value">{exits}</div>
                                <div className="hc-trend-bar exit" style={{ height: exitH }} />
                              </div>
                            )}
                          </div>
                          <div className="hc-trend-column-label">
                            {["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][Number(item.month.slice(5)) - 1]}
                            <div className="hc-trend-net" style={{ color: item.net_change >= 0 ? "#10b981" : "#ef4444" }}>
                              {item.net_change >= 0 ? "+" : ""}{item.net_change}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="hc-note">No monthly trend data available for {trendCategory === "engineering" ? "Engineering" : trendCategory === "support" ? "Support" : ""} employees in {trendYear}.</p>
                )}
              </section>
            )}

          </>
        ) : null}
      </div>
    </div>
  );
}

function KpiCard({ label, value, showPlus, icon: Icon, className = "", onClick, breakdown, onBreakdownClick }) {
  const hasBreakdown = Array.isArray(breakdown?.rows) && breakdown.rows.length > 0;
  return (
    <div className={`hc-kpi-card ${className} ${onClick ? "hc-kpi-clickable" : ""}`} onClick={onClick}>
      <div className="hc-kpi-icon-area">
        {Icon && <Icon />}
      </div>
      <div className="hc-kpi-body">
        <div className="hc-kpi-value">{value == null ? "-" : `${showPlus && value >= 0 ? "+" : ""}${value}`}</div>
        <div className="hc-kpi-label">{label}</div>
      </div>
      {hasBreakdown && (
        <div className="hc-kpi-breakdown">
          {breakdown.rows.map((row) => (
            row.value > 0 && (
              <div
                key={row.kind}
                className="hc-kpi-chip"
                title={row.label}
                onClick={(e) => {
                  e.stopPropagation();
                  onBreakdownClick?.(row.kind, row.filters);
                }}
              >
                <span className="hc-kpi-chip-value">{row.value}</span>
                <span className="hc-kpi-chip-label">{row.short}</span>
              </div>
            )
          ))}
        </div>
      )}
    </div>
  );
}

function JoinExitTicker({ joiners, exits, joinsForPeriod, exitsForPeriod }) {
  const navigate = useNavigate();
  const joinLabel = `Joiners this month: ${joinsForPeriod}`;
  const exitLabel = `Exits this month: ${exitsForPeriod}`;

  return (
    <div className="hc-ticker-bar">
      <div className="hc-ticker-container">
          <div className="hc-ticker-section join">
          <div className="hc-ticker-header"><PersonAddAltIcon className="hc-ticker-icon join" /> {joinLabel}</div>
          <div className="hc-ticker-track">
            <div className="hc-ticker-items">
              {joiners.length > 0 ? (
                <>
                  {[...joiners, ...joiners, ...joiners].map((emp, i) => (
                    <div key={`j-${emp.employee_id}-${i}`} className="hc-ticker-item join" onClick={() => navigate(`/employees/${emp.employee_id}`, { state: { fromHeadcount: true } })} style={{cursor: 'pointer'}}>
                      <span className="hc-ticker-badge join">+</span>
                      <span className="hc-ticker-name">{emp.employee_id}</span>
                    </div>
                  ))}
                </>
              ) : (
                <div className="hc-ticker-item join">
                  <span className="hc-ticker-badge join">+</span>
                  <span className="hc-ticker-name">No joiners this period</span>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="hc-ticker-section exit">
          <div className="hc-ticker-header"><PersonRemoveIcon className="hc-ticker-icon exit" /> {exitLabel}</div>
          <div className="hc-ticker-track">
            <div className="hc-ticker-items hc-ticker-items-fast">
              {exits.length > 0 ? (
                <>
                  {[...exits, ...exits, ...exits].map((emp, i) => (
                    <div key={`e-${emp.employee_id}-${i}`} className="hc-ticker-item exit" onClick={() => navigate(`/employees/${emp.employee_id}`, { state: { fromHeadcount: true } })} style={{cursor: 'pointer'}}>
                      <span className="hc-ticker-badge exit">-</span>
                      <span className="hc-ticker-name">{emp.employee_id}</span>
                    </div>
                  ))}
                </>
              ) : (
                <div className="hc-ticker-item exit">
                  <span className="hc-ticker-badge exit">-</span>
                  <span className="hc-ticker-name">No exits this period</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SummaryBlock({ title, items = [], onItemClick, showAll }) {
  const [expanded, setExpanded] = useState(false);
  const effectiveShowAll = showAll || expanded;
  const display = effectiveShowAll
    ? [...(items || [])].sort((a, b) => b.value - a.value)
    : [...(items || [])].sort((a, b) => b.value - a.value).slice(0, 4);
  const hasMore = items.length > 4;
  return (
    <div className="hc-summary-block">
      <div className="hc-summary-title">{title}</div>
      {display.length ? display.map((item) => (
        <div key={item.label} className="hc-summary-row hc-summary-row-clickable" onClick={() => onItemClick?.(item.label)}>
          <span>{item.label}</span>
          <strong>{item.value}</strong>
        </div>
      )) : <p className="hc-note">No data</p>}
      {hasMore && !showAll && (
        <button className="hc-view-all-link" onClick={() => setExpanded((v) => !v)}>
          {expanded ? "Show less" : "View all"}
        </button>
      )}
    </div>
  );
}

function CategoryToggle({ selected, onChange }) {
  const options = [
    { value: "all", label: "All" },
    { value: "engineering", label: "Engineering" },
    { value: "support", label: "Support" },
  ];
  return (
    <div className="hc-header-pills">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          className={`hc-pill ${selected === opt.value ? "active" : ""}`}
          onClick={() => onChange(opt.value)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function DepartmentSummaryCard({ icon, title, total, divisions, onDivisionClick }) {
  return (
    <div className="hc-dept-summary-card">
      <div className="hc-dept-summary-header">
        <div className="hc-dept-summary-title">
          {icon}
          <span>{title}</span>
        </div>
        <strong className="hc-dept-summary-total">{total}</strong>
      </div>
      <div className="hc-dept-summary-body">
        {divisions.length > 0 ? divisions.map((div) => (
          <div key={div.label} className="hc-dept-summary-row hc-dept-summary-row-clickable" onClick={() => onDivisionClick?.(div.label)}>
            <span><AccountTreeIcon className="hc-dept-icon" /> {div.label}</span>
            <strong>{div.value}</strong>
          </div>
        )) : (
          <p className="hc-note">No divisions found</p>
        )}
      </div>
    </div>
  );
}

function EngineeringOverviewPanel({ tree, expandedDivisions, expandedDepartments, toggleDivision, toggleDepartment, onDivisionClick, onDepartmentClick, onPeopleClick }) {
  const engineering = tree.engineering || { label: "Engineering", value: 0, children: [] };
  return (
    <div className="hc-overview-engineering">
      <div className="hc-dept-side-panel-header">
        <span>Engineering</span>
        <strong>{engineering.value}</strong>
      </div>
      <OrgHierarchyList
        group={engineering}
        emptyText="No engineering departments found"
        expandedDivisions={expandedDivisions}
        expandedDepartments={expandedDepartments}
        toggleDivision={toggleDivision}
        toggleDepartment={toggleDepartment}
        groupKey="engineering"
        onDivisionClick={onDivisionClick}
        onDepartmentClick={onDepartmentClick}
        onPeopleClick={onPeopleClick}
      />
    </div>
  );
}

function SupportOverviewPanel({ tree, expandedDivisions, expandedDepartments, toggleDivision, toggleDepartment, onDivisionClick, onDepartmentClick, onPeopleClick }) {
  const support = tree.support || { label: "Support", value: 0, children: [] };
  return (
    <div className="hc-overview-engineering">
      <div className="hc-dept-side-panel-header">
        <span>Support</span>
        <strong>{support.value}</strong>
      </div>
      <OrgHierarchyList
        group={support}
        emptyText="No support departments found"
        expandedDivisions={expandedDivisions}
        expandedDepartments={expandedDepartments}
        toggleDivision={toggleDivision}
        toggleDepartment={toggleDepartment}
        groupKey="support"
        onDivisionClick={onDivisionClick}
        onDepartmentClick={onDepartmentClick}
        onPeopleClick={onPeopleClick}
      />
    </div>
  );
}

function SupportBreakdownCompact({ tree }) {
  const support = tree.support || { label: "Support", value: 0, children: [] };
  const divisions = support.children || [];

  if (!divisions.length) {
    return <p className="hc-note">No support departments found</p>;
  }

  const mid = Math.ceil(divisions.length / 2);
  const leftCol = divisions.slice(0, mid);
  const rightCol = divisions.slice(mid);

  const renderColumn = (colDivisions) => (
    <div className="hc-support-col">
      {colDivisions.map((div) => (
        <div key={div.label} className="hc-support-col-division">
          <div className="hc-support-col-division-header">
            <span>{div.label}</span>
            <strong>{div.value}</strong>
          </div>
          {div.children && div.children.length > 0 && (
            <div className="hc-support-col-depts">
                  {div.children.map((dept) => (
                    <div key={dept.label} className="hc-support-col-dept">
                      <span><AccountTreeIcon className="hc-dept-icon" /> {dept.label}</span>
                      <strong>{dept.value}</strong>
                    </div>
                  ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );

  return (
    <div className="hc-support-col-layout">
      {renderColumn(leftCol)}
      {renderColumn(rightCol)}
    </div>
  );
}

function OrgHierarchyList({ group, emptyText, expandedDivisions, expandedDepartments, toggleDivision, toggleDepartment, groupKey, onDivisionClick, onDepartmentClick, onPeopleClick }) {
  const divisions = group.children || [];
  return (
    <div className="hc-dept-sublist">
      {divisions.length ? divisions.map((division) => {
        const divisionKey = `${groupKey}:${division.label}`;
        const expanded = Boolean(expandedDivisions[divisionKey]);
        return (
          <div key={division.label} className="hc-division-group">
            <div className={`hc-accordion-header ${expanded ? "expanded" : ""}`}>
              <span className="hc-accordion-label" onClick={() => onDivisionClick?.(division.label)}>
                {division.label}
              </span>
              <div className="hc-accordion-meta">
                <strong>{division.value}</strong>
                <button type="button" className="hc-accordion-arrow-btn" onClick={() => toggleDivision(divisionKey)}>
                  <span className="hc-accordion-arrow">{expanded ? "▾" : "▸"}</span>
                </button>
              </div>
            </div>
            <div className={`hc-accordion-panel ${expanded ? "open" : ""}`}>
              {(division.children || []).map((department) => {
                const deptKey = `${divisionKey}:${department.label}`;
                const deptOpen = Boolean(expandedDepartments[deptKey]);
                const breakdownRows = [
                  { kind: "employee", label: "Employees", value: department.employees || 0 },
                  { kind: "trainee", label: "Trainees", value: department.interns || department.trainees || 0 },
                  { kind: "pt", label: "PT / Secondment", value: department.pt || 0 },
                ].filter((row) => row.value > 0);
                return (
                  <div key={department.label} className="hc-dept-group">
                    <div className={`hc-dept-mini-header ${deptOpen ? "open" : ""}`}>
                      <span className="hc-dept-mini-label" onClick={() => onDepartmentClick?.(department.label)}>
                        <AccountTreeIcon className="hc-dept-icon" /> {department.label}
                      </span>
                      <div className="hc-dept-mini-meta">
                        <strong>{department.value}</strong>
                        <button
                          type="button"
                          className="hc-accordion-arrow-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleDepartment(deptKey);
                          }}
                        >
                          <span className="hc-accordion-arrow">{deptOpen ? "▾" : "▸"}</span>
                        </button>
                      </div>
                    </div>
                    <div className={`hc-dept-mini-panel ${deptOpen ? "open" : ""}`}>
                      {breakdownRows.length > 0 ? breakdownRows.map((row) => (
                        <div
                          key={`${deptKey}-${row.kind}`}
                          className="hc-dept-count-row"
                          onClick={() => onPeopleClick?.(row.kind, {
                            department: department.label,
                            division: division.label,
                            category: groupKey === "engineering" ? "engineering" : groupKey === "support" ? "support" : "",
                          })}
                        >
                          <span>{row.label}</span>
                          <strong>{row.value}</strong>
                        </div>
                      )) : <div className="hc-dept-count-row hc-dept-count-empty">No breakdown</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      }) : <p className="hc-note">{emptyText}</p>}
    </div>
  );
}

function FilterPanel({ title, data, filterKey, selected, setSelected }) {
  return (
    <section className="hc-panel">
      <div className="hc-panel-title">{title}</div>
      <div className="hc-panel-list">
        {data.length ? data.map((item) => (
          item.children ? (
            <div key={item.label} className="hc-dept-filter-group">
              <div className="hc-dept-filter-group-title">
                <span>{item.label}</span>
                <strong>{item.value}</strong>
              </div>
              <NestedFilterItems item={item} filterKey={filterKey} selected={selected} setSelected={setSelected} />
            </div>
          ) : (
            <button
              type="button"
              key={`${filterKey}-${item.label}`}
              className={tileClass(selected, filterKey, item.label)}
              onClick={() => toggleFilter(selected, setSelected, filterKey, item.label)}
            >
              <span>{item.label}</span>
              <strong>{item.value}</strong>
            </button>
          )
        )) : <p className="hc-note">No data</p>}
      </div>
    </section>
  );
}

function NestedFilterItems({ item, filterKey, selected, setSelected }) {
  return (
    <div className="hc-dept-filter-items">
      {item.children.map((child) => (
        child.children ? (
          <div key={`${filterKey}-${item.label}-${child.label}`} className="hc-dept-filter-group hc-filter-nested-group">
            <div className="hc-dept-filter-group-title">
              <span>{child.label}</span>
              <strong>{child.value}</strong>
            </div>
            <NestedFilterItems item={child} filterKey={filterKey} selected={selected} setSelected={setSelected} />
          </div>
        ) : (
          <button
            type="button"
            key={`${filterKey}-${item.label}-${child.label}`}
            className={tileClass(selected, filterKey, child.label)}
            onClick={() => toggleFilter(selected, setSelected, filterKey, child.label)}
          >
            <span>{child.label}</span>
            <strong>{child.value}</strong>
          </button>
        )
      ))}
    </div>
  );
}

function toItems(values) {
  return [...values].sort((a, b) => b.value - a.value);
}

function tileClass(selected, key, value) {
  const active = selected.key === key && selected.value === value;
  return `hc-tile ${active ? "active" : ""}`.trim();
}

function toggleFilter(selected, setSelected, key, value) {
  if (selected.key === key && selected.value === value) {
    setSelected({ key: null, value: null });
    return;
  }
  setSelected({ key, value });
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function normalizeEmploymentType(value) {
  if (!value) return "Unspecified";
  const upper = String(value).toUpperCase().trim();
  // Word-boundary matching so "Internal" is NOT mistaken for "Intern".
  if (/\bINTERN(S|SHIP)?\b/.test(upper)) return "Intern";
  if (/\bTRAINEE(S|SHIP)?\b/.test(upper)) return "Trainee";
  if (upper.includes("PERM")) return "Permanent";
  if (upper.includes("CONSULTANT")) return "Consultant";
  return String(value).trim() || "Unspecified";
}

function buildBreakdownsFromEmployees(employees = []) {
  const keys = ["department", "seating_location", "gender", "employment_type", "business_unit", "company", "division"];
  const maps = {};
  keys.forEach((key) => { maps[key] = new Map(); });

  for (let i = 0; i < employees.length; i++) {
    const employee = employees[i];
    for (let k = 0; k < keys.length; k++) {
      const key = keys[k];
      const raw = key === "division" ? getDivision(employee) : employee?.[key];
      const value = key === "employment_type" ? normalizeEmploymentType(raw) : raw;
      const label = String(value == null || value === "" ? "Unspecified" : value);
      const map = maps[key];
      map.set(label, (map.get(label) || 0) + 1);
    }
  }

  const result = {};
  keys.forEach((key) => {
    result[`by_${key}`] = Array.from(maps[key].entries()).map(([l, v]) => ({ label: l, value: v }));
  });
  return result;
}

function buildOrgHierarchy(employees = []) {
  const businessUnits = new Map();

  employees.forEach((employee) => {
    const bu = String(employee?.business_unit || "Unspecified");
    const division = String(getDivision(employee) || "Unspecified");
    const department = String(employee?.department || "Unspecified");

    if (!businessUnits.has(bu)) {
      businessUnits.set(bu, { label: bu, value: 0, childrenMap: new Map() });
    }
    const buGroup = businessUnits.get(bu);
    buGroup.value += 1;

    if (!buGroup.childrenMap.has(division)) {
      buGroup.childrenMap.set(division, { label: division, value: 0, childrenMap: new Map() });
    }
    const divisionGroup = buGroup.childrenMap.get(division);
    divisionGroup.value += 1;

    if (!divisionGroup.childrenMap.has(department)) {
      divisionGroup.childrenMap.set(department, { label: department, value: 0 });
    }
    divisionGroup.childrenMap.get(department).value += 1;
  });

  return Array.from(businessUnits.values()).map((bu) => ({
    label: bu.label,
    value: bu.value,
    children: Array.from(bu.childrenMap.values()).map((division) => ({
      label: division.label,
      value: division.value,
      children: Array.from(division.childrenMap.values()).sort(sortByValueThenLabel),
    })).sort(sortByValueThenLabel),
  })).sort(sortByValueThenLabel);
}

function buildOrgTree(employees = []) {
  const groups = {
    engineering: { label: "Engineering", value: 0, divisions: new Map() },
    support: { label: "Support", value: 0, divisions: new Map() },
  };

  employees.forEach((employee) => {
    const isEngineer = isEngineeringEmployee(employee);
    const isSupporter = isSupportEmployee(employee);
    if (!isEngineer && !isSupporter) return;

    const division = String(getDivision(employee) || "Unspecified");
    const department = String(employee?.department || "Unspecified");
    const target = isEngineer ? groups.engineering : groups.support;
    const kind = isInternRecord(employee) ? "interns" : isPTEmployee(employee) ? "pt" : "employees";
    target.value += 1;

    if (!target.divisions.has(division)) {
      target.divisions.set(division, { label: division, value: 0, departments: new Map() });
    }
    const divisionGroup = target.divisions.get(division);
    divisionGroup.value += 1;

    if (!divisionGroup.departments.has(department)) {
      divisionGroup.departments.set(department, { label: department, value: 0, employees: 0, interns: 0, pt: 0 });
    }
    const deptGroup = divisionGroup.departments.get(department);
    deptGroup.value += 1;
    deptGroup[kind] += 1;
  });

  const formatGroup = (group) => ({
    label: group.label,
    value: group.value,
    children: Array.from(group.divisions.values())
      .map((division) => ({
        label: division.label,
        value: division.value,
        children: Array.from(division.departments.values()).sort(sortByValueThenLabel),
      }))
      .sort(sortByValueThenLabel),
  });

  return {
    engineering: formatGroup(groups.engineering),
    support: formatGroup(groups.support),
  };
}

function sortByValueThenLabel(a, b) {
  return b.value - a.value || String(a.label).localeCompare(String(b.label));
}

function findOrgGroup(groups, matcher) {
  return groups.find((group) => matcher(group.label));
}

function isEngineeringEmployee(employee) {
  return isEngineeringLabel(employee?.business_unit) || isEngineeringLabel(getDivision(employee)) || isEngineeringLabel(employee?.department);
}

function isSupportEmployee(employee) {
  // Prevent double-counting: if already classified as Engineer, don't also classify as Support
  if (isEngineeringEmployee(employee)) return false;
  return isSupportLabel(employee?.business_unit) || isSupportLabel(getDivision(employee)) || isSupportLabel(employee?.department);
}

function isEngineeringLabel(label) {
  const text = String(label || "").toUpperCase();
  return text === "COR" || /\b(COR|ENG|ENGINEER|ENGINEERING)\b/.test(text);
}

function isSupportLabel(label) {
  const text = String(label || "").toUpperCase();
  return text === "SGA" || /\b(SGA|SUPPORT|SERVICE|SERVIC)\b/.test(text);
}

function getDivision(employee) {
  return employee?.division || employee?.division_name || employee?.department_group || employee?.business_division || "";
}

function matchesPeriod(employee, year, month) {
  if (!year && !month) return true;
  const joinDate = getFirstDate(employee, JOIN_DATE_KEYS);
  const exitDate = getFirstDate(employee, EXIT_DATE_KEYS);
  if (!joinDate && !exitDate) return true;

  const targetYear = Number(year || new Date().getFullYear());
  const targetMonth = Number(month || "12") - 1;
  const start = new Date(targetYear, Number(month ? targetMonth : 0), 1);
  const end = new Date(targetYear, targetMonth + 1, 0, 23, 59, 59, 999);

  if (joinDate && joinDate > end) return false;
  if (exitDate && exitDate < start) return false;
  return true;
}

function countEmployeesForDate(employees, dateKeys, year, month, fallback) {
  if (!year || !month) return fallback ?? 0;
  const count = employees.reduce((sum, employee) => {
    const date = getFirstDate(employee, dateKeys);
    if (!date) return sum;
    const matches = String(date.getFullYear()) === String(year) && String(date.getMonth() + 1).padStart(2, "0") === String(month);
    return matches ? sum + 1 : sum;
  }, 0);
  return count || fallback || 0;
}

function buildTrendData(employees, year) {
  const months = {};
  for (let m = 1; m <= 12; m++) {
    const key = `${year}-${String(m).padStart(2, "0")}`;
    months[key] = { month: key, joins: 0, exits: 0, net_change: 0 };
  }

  employees.forEach((e) => {
    const joinDate = getFirstDate(e, JOIN_DATE_KEYS);
    const exitDate = getFirstDate(e, EXIT_DATE_KEYS);

    if (joinDate && String(joinDate.getFullYear()) === String(year)) {
      const key = `${year}-${String(joinDate.getMonth() + 1).padStart(2, "0")}`;
      if (months[key]) months[key].joins += 1;
    }
    if (exitDate && String(exitDate.getFullYear()) === String(year)) {
      const key = `${year}-${String(exitDate.getMonth() + 1).padStart(2, "0")}`;
      if (months[key]) months[key].exits += 1;
    }
  });

  return Object.values(months).map((m) => ({
    ...m,
    net_change: m.joins - m.exits,
  }));
}

function countActiveAtEndOfMonth(employees, year, month) {
  if (!year || !month) return employees.length;
  const targetYear = Number(year);
  const targetMonth = Number(month) - 1;
  const endOfMonth = new Date(targetYear, targetMonth + 1, 0, 23, 59, 59, 999);

  return employees.filter((e) => {
    const joinDate = getFirstDate(e, JOIN_DATE_KEYS);
    const exitDate = getFirstDate(e, EXIT_DATE_KEYS);
    if (joinDate && joinDate > endOfMonth) return false;
    if (exitDate && exitDate <= endOfMonth) return false;
    return true;
  }).length;
}

function getFirstDate(employee, keys) {
  for (const key of keys) {
    const value = employee?.[key];
    const date = parseMaybeDate(value);
    if (date) return date;
  }
  return null;
}

function parseMaybeDate(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function getEmployeeYears(employee) {
  return [...JOIN_DATE_KEYS, ...EXIT_DATE_KEYS].reduce((years, key) => {
    const date = parseMaybeDate(employee?.[key]);
    if (date) years.push(String(date.getFullYear()));
    return years;
  }, []);
}

const PT_EMPLOYEE_STATUS = "Secondment (Payroll Transfer)";

// Intern + PT classification ------------------------------------------------------------------

function isTraineeRecord(employee) {
  if (!employee) return false;
  if (employee._headcountType === "trainee" || employee._headcountType === "intern") return true;
  // Zoho is the source of truth: intern/trainee rows arrive as ordinary
  // employees whose employment_type contains INTERN or TRAINEE. Word-boundary
  // matching keeps "Internal" from being misclassified as "Intern".
  const type = String(employee.employment_type || "").toUpperCase();
  return /\bINTERN(S|SHIP)?\b/.test(type) || /\bTRAINEE(S|SHIP)?\b/.test(type);
}
function isInternRecord(employee) {
  return isTraineeRecord(employee);
}

function isPTEmployee(employee) {
  if (isInternRecord(employee)) return false;
  const status = String(employee?.employee_status || "").toUpperCase();
  const type = String(employee?.employment_type || "").toUpperCase();
  if (/\b(SECONDMENT|PAYROLL TRANSFER)\b/.test(status)) return true;
  if (status === "PT") return true;
  if (/\b(SECONDMENT|PAYROLL TRANSFER|PT)\b/.test(type)) return true;
  return false;
}

// Intern records no longer carry a separate schema — they are Zoho employee
// rows classified by employment_type (see isTraineeRecord).

// Splits a person list into Employee / Trainee / PT buckets for a KPI card.
function splitBreakdown(people = [], baseFilters = {}) {
  const rows = [];
  let employees = 0, interns = 0, pt = 0;
  for (let i = 0; i < people.length; i++) {
    const person = people[i];
    if (isInternRecord(person)) interns += 1;
    else if (isPTEmployee(person)) pt += 1;
    else employees += 1;
  }
  const makeRow = (kind, short, label, value) => ({ kind, short, label, value, filters: { ...baseFilters } });
  if (employees > 0) rows.push(makeRow("employee", "Emp", "Employees", employees));
  if (interns > 0) rows.push(makeRow("trainee", "Tr", "Trainees", interns));
  if (pt > 0) rows.push(makeRow("pt", "PT", "PT / Secondment", pt));
  return { total: employees + interns + pt, rows, employees, interns, pt };
}

// Employment Type panel labels (Employee / Consultant / Trainee / Secondment-PT).
function classifyEmploymentType(employee) {
  const type = normalizeEmploymentType(employee?.employment_type);
  if (isInternRecord(employee)) return "Trainee";
  if (isPTEmployee(employee)) return "Secondment / PT";
  const upperType = String(type).toUpperCase();
  if (upperType.includes("CONSULTANT") || upperType.includes("CONTRACT") || upperType.includes("VENDOR")) return "Consultant";
  if (upperType === "PERMANENT" || upperType === "PERM" || type === "Employee") return "Employee";
  return type === "Unspecified" ? "Unspecified" : "Employee";
}

// Maps an Employment Type panel label to the Employees page employmentType filter.
function mapEmploymentTypeRoute(label) {
  const upper = String(label || "").toUpperCase();
  if (upper.includes("INTERN")) return "Intern";
  if (upper.includes("CONSULTANT") || upper.includes("CONTRACT") || upper.includes("VENDOR")) return "Consultant,Contract,Vendor";
  if (upper.includes("SECONDMENT") || upper.includes("PT")) return null;
  if (upper === "UNSPECIFIED") return "Unspecified";
  return "PERM";
}


